import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Period } from '@spike/model';
import clsx from 'clsx';
import styles from 'components/UI/V3/DatePicker/DatePicker.module.scss';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import Calendar from 'react-calendar';
import { Value } from 'react-calendar/dist/cjs/shared/types';

interface DatePickerBaseProps {
    minDate?: Moment;
    maxDate?: Moment;
    timeZone: string;
    className?: string;
}

interface DatePickerPropsRange extends DatePickerBaseProps {
    rangeMode: true;
    value?: Period | null;
    onChange?: (value: Period | null) => void;
}

interface DatePickerPropsSingle extends DatePickerBaseProps {
    rangeMode?: false;
    value?: Moment | null;
    onChange?: (value: Moment | null) => void;
}

type DatePickerProps = DatePickerPropsRange | DatePickerPropsSingle;

const convertFromMomentToDate = (moment: Moment): Date => {
    return new Date(moment.year(), moment.month(), moment.date());
};

export const DatePicker: React.FC<DatePickerProps> = ({
    minDate,
    maxDate,
    value,
    rangeMode,
    timeZone,
    className,
    onChange
}) => {
    const changeHandler = (calendarValue: Value) => {
        if (!calendarValue || calendarValue === null) {
            onChange?.(null);
            return;
        }

        if (!rangeMode && calendarValue instanceof Date) {
            // Single mode: expect single date
            const singleMoment = moment(calendarValue).tz(timeZone, true);

            return onChange?.(singleMoment);
        }

        if (rangeMode && Array.isArray(calendarValue)) {
            let endMoment: Moment | null = null;
            const startMoment = moment(calendarValue[0]).tz(timeZone, true);

            if (calendarValue[1] !== null) {
                endMoment = moment(calendarValue[1]).tz(timeZone, true);
            } else {
                endMoment = startMoment.clone();
            }

            onChange?.({ from: startMoment, to: endMoment });
        }
    };

    const calendarValue = React.useMemo(() => {
        if (!value) {
            return null;
        }

        // Single mode: value should be Date
        if (!rangeMode) {
            return convertFromMomentToDate(value);
        }

        const startDateMoment = value.from;
        const endDateMoment = value.to;

        const rangeValue: [Date, Date] = [
            convertFromMomentToDate(startDateMoment),
            convertFromMomentToDate(endDateMoment)
        ];

        return rangeValue;
    }, [value, rangeMode]);

    return (
        <div className={styles.root}>
            <Calendar
                minDetail="year"
                calendarType="gregory"
                prev2Label={null}
                next2Label={null}
                allowPartialRange
                minDate={minDate?.toDate()}
                maxDate={maxDate?.toDate()}
                selectRange={rangeMode}
                showNeighboringMonth={false}
                className={clsx(styles.calendar, className)}
                prevLabel={<FontAwesomeIcon icon={faAngleLeft} />}
                nextLabel={<FontAwesomeIcon icon={faAngleRight} />}
                formatMonth={(locale, date) => date.toLocaleString(locale, { month: 'short' })}
                value={calendarValue}
                onChange={changeHandler}
            />
        </div>
    );
};
